import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { formatDateTimeForInput, getNowAEST } from '../../utils/dateUtils';
import { X, Clock, Users, Building2, Calendar, MapPin, Timer, Coffee, Trash2, Plus, DollarSign } from 'lucide-react';

interface Worker {
  id: string;
  name: string;
  role: string;
  email: string;
  phone?: string;
  status: string;
  hourly_pay_rate?: number;
  overtime_pay_rate?: number;
}

interface Site {
  id: string;
  name: string;
  address: string;
  type: string;
  status: string;
}

interface Break {
  id: string;
  timesheet_id: string;
  worker_id: string;
  break_type: 'break' | 'lunch';
  start_time: string;
  end_time?: string;
  duration_minutes?: number;
  created_at: string;
  updated_at: string;
}

interface Timesheet {
  id: string;
  worker_id: string;
  site_id: string;
  clock_in_time: string;
  clock_out_time?: string;
  clock_in_latitude?: number;
  clock_in_longitude?: number;
  clock_out_latitude?: number;
  clock_out_longitude?: number;
  total_hours?: number;
  hourly_rate_used?: number;
  overtime_rate_used?: number;
  regular_hours?: number;
  overtime_hours?: number;
  total_pay?: number;
  status: 'active' | 'completed';
  created_at: string;
  updated_at: string;
  worker?: Worker;
  site?: Site;
  breaks?: Break[];
}

interface TimesheetFormProps {
  timesheet?: Timesheet | null;
  onSubmit: (timesheetData: Partial<Timesheet>) => Promise<void>;
  onClose: () => void;
  loading?: boolean;
}

export const TimesheetForm: React.FC<TimesheetFormProps> = ({ 
  timesheet, 
  onSubmit, 
  onClose, 
  loading = false 
}) => {
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [breaks, setBreaks] = useState<Break[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  const [deletingBreak, setDeletingBreak] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    worker_id: timesheet?.worker_id || '',
    site_id: timesheet?.site_id || '',
    clock_in_time: timesheet?.clock_in_time ? formatDateTimeForInput(timesheet.clock_in_time) : getNowAEST(),
    clock_out_time: timesheet?.clock_out_time ? formatDateTimeForInput(timesheet.clock_out_time) : '',
    clock_in_latitude: timesheet?.clock_in_latitude || '',
    clock_in_longitude: timesheet?.clock_in_longitude || '',
    clock_out_latitude: timesheet?.clock_out_latitude || '',
    clock_out_longitude: timesheet?.clock_out_longitude || '',
    hourly_rate_used: timesheet?.hourly_rate_used?.toString() || '',
    overtime_rate_used: timesheet?.overtime_rate_used?.toString() || '',
    regular_hours: timesheet?.regular_hours?.toString() || '',
    overtime_hours: timesheet?.overtime_hours?.toString() || '',
    status: timesheet?.status || 'active'
  });

  const [newBreak, setNewBreak] = useState({
    break_type: 'break' as 'break' | 'lunch',
    start_time: getNowAEST(),
    end_time: '',
    duration_minutes: 0
  });

  const [showAddBreak, setShowAddBreak] = useState(false);

  useEffect(() => {
    fetchData();
    if (timesheet?.id) {
      fetchBreaks();
    }
  }, [timesheet]);

  const fetchData = async () => {
    try {
      const [workersData, sitesData] = await Promise.all([
        supabase.from('workers').select('*').eq('status', 'active').order('name'),
        supabase.from('sites').select('*').eq('status', 'active').order('name')
      ]);

      if (workersData.data) setWorkers(workersData.data);
      if (sitesData.data) setSites(sitesData.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoadingData(false);
    }
  };

  const fetchBreaks = async () => {
    if (!timesheet?.id) return;

    try {
      const { data, error } = await supabase
        .from('breaks')
        .select('*')
        .eq('timesheet_id', timesheet.id)
        .order('start_time');

      if (error) throw error;
      setBreaks(data || []);
    } catch (error) {
      console.error('Error fetching breaks:', error);
    }
  };

  const calculateTotalHours = () => {
    if (!formData.clock_in_time || !formData.clock_out_time) return 0;
    
    const clockIn = new Date(formData.clock_in_time);
    const clockOut = new Date(formData.clock_out_time);
    const diffMs = clockOut.getTime() - clockIn.getTime();
    const hours = diffMs / (1000 * 60 * 60);
    
    return Math.max(0, hours);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Calculate total hours
    const totalHours = calculateTotalHours();

    const regularHours = formData.regular_hours ? parseFloat(formData.regular_hours) : null;
    const overtimeHours = formData.overtime_hours ? parseFloat(formData.overtime_hours) : null;
    const hourlyRate = formData.hourly_rate_used ? parseFloat(formData.hourly_rate_used) : null;
    const overtimeRate = formData.overtime_rate_used ? parseFloat(formData.overtime_rate_used) : null;

    let totalPay = null;
    if (regularHours && hourlyRate) {
      totalPay = regularHours * hourlyRate;
      if (overtimeHours && overtimeRate) {
        totalPay += overtimeHours * overtimeRate;
      }
    }

    const timesheetData = {
      ...formData,
      clock_in_latitude: formData.clock_in_latitude ? Number(formData.clock_in_latitude) : null,
      clock_in_longitude: formData.clock_in_longitude ? Number(formData.clock_in_longitude) : null,
      clock_out_latitude: formData.clock_out_latitude ? Number(formData.clock_out_latitude) : null,
      clock_out_longitude: formData.clock_out_longitude ? Number(formData.clock_out_longitude) : null,
      hourly_rate_used: hourlyRate,
      overtime_rate_used: overtimeRate,
      regular_hours: regularHours,
      overtime_hours: overtimeHours,
      total_pay: totalPay,
      total_hours: totalHours > 0 ? totalHours : null,
      clock_in_time: new Date(formData.clock_in_time).toISOString(),
      clock_out_time: formData.clock_out_time ? new Date(formData.clock_out_time).toISOString() : null
    };

    await onSubmit(timesheetData);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    setFormData(prev => {
      const updated = {
        ...prev,
        [name]: value
      };

      if (name === 'worker_id' && value) {
        const selectedWorker = workers.find(w => w.id === value);
        if (selectedWorker) {
          if (!formData.hourly_rate_used && selectedWorker.hourly_pay_rate) {
            updated.hourly_rate_used = selectedWorker.hourly_pay_rate.toString();
          }
          if (!formData.overtime_rate_used && selectedWorker.overtime_pay_rate) {
            updated.overtime_rate_used = selectedWorker.overtime_pay_rate.toString();
          }
        }
      }

      return updated;
    });
  };

  const handleAddBreak = async () => {
    if (!timesheet?.id || !newBreak.start_time) return;

    try {
      // Calculate duration if both start and end times are provided
      let duration = 0;
      if (newBreak.start_time && newBreak.end_time) {
        const start = new Date(newBreak.start_time);
        const end = new Date(newBreak.end_time);
        duration = Math.max(0, (end.getTime() - start.getTime()) / (1000 * 60)); // minutes
      }

      const breakData = {
        timesheet_id: timesheet.id,
        worker_id: timesheet.worker_id,
        break_type: newBreak.break_type,
        start_time: new Date(newBreak.start_time).toISOString(),
        end_time: newBreak.end_time ? new Date(newBreak.end_time).toISOString() : null,
        duration_minutes: duration > 0 ? duration : null
      };

      const { error } = await supabase
        .from('breaks')
        .insert([breakData]);

      if (error) throw error;

      // Reset form and refresh breaks
      setNewBreak({
        break_type: 'break',
        start_time: '',
        end_time: '',
        duration_minutes: 0
      });
      setShowAddBreak(false);
      await fetchBreaks();
    } catch (error) {
      console.error('Error adding break:', error);
      alert('Failed to add break. Please try again.');
    }
  };

  const handleDeleteBreak = async (breakId: string) => {
    if (!confirm('Are you sure you want to delete this break?')) return;

    setDeletingBreak(breakId);
    try {
      const { error } = await supabase
        .from('breaks')
        .delete()
        .eq('id', breakId);

      if (error) throw error;
      await fetchBreaks();
    } catch (error) {
      console.error('Error deleting break:', error);
      alert('Failed to delete break. Please try again.');
    } finally {
      setDeletingBreak(null);
    }
  };

  const formatDateTime = (dateTimeString: string) => {
    return new Date(dateTimeString).toISOString().slice(0, 16);
  };

  const selectedWorker = workers.find(w => w.id === formData.worker_id);
  const selectedSite = sites.find(s => s.id === formData.site_id);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Clock className="h-5 w-5 text-blue-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900">
              {timesheet ? 'Edit Timesheet' : 'Add New Timesheet'}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Worker and Site Selection */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Users className="inline h-4 w-4 mr-1" />
                Worker *
              </label>
              {loadingData ? (
                <div className="flex items-center justify-center p-3 border border-gray-300 rounded-md">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                  <span className="ml-2 text-sm text-gray-600">Loading...</span>
                </div>
              ) : (
                <select
                  name="worker_id"
                  required
                  value={formData.worker_id}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <option value="">Select a worker</option>
                  {workers.map(worker => (
                    <option key={worker.id} value={worker.id}>
                      {worker.name} - {worker.role}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Building2 className="inline h-4 w-4 mr-1" />
                Site *
              </label>
              {loadingData ? (
                <div className="flex items-center justify-center p-3 border border-gray-300 rounded-md">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-500"></div>
                  <span className="ml-2 text-sm text-gray-600">Loading...</span>
                </div>
              ) : (
                <select
                  name="site_id"
                  required
                  value={formData.site_id}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <option value="">Select a site</option>
                  {sites.map(site => (
                    <option key={site.id} value={site.id}>
                      {site.name} - {site.address}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          {/* Selected Worker and Site Info */}
          {(selectedWorker || selectedSite) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {selectedWorker && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-medium text-blue-900 mb-2">Worker Information</h4>
                  <div className="text-sm text-blue-800 space-y-1">
                    <p><strong>Name:</strong> {selectedWorker.name}</p>
                    <p><strong>Role:</strong> {selectedWorker.role.replace('_', ' ')}</p>
                    <p><strong>Email:</strong> {selectedWorker.email}</p>
                  </div>
                </div>
              )}

              {selectedSite && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <h4 className="font-medium text-orange-900 mb-2">Site Information</h4>
                  <div className="text-sm text-orange-800 space-y-1">
                    <p><strong>Name:</strong> {selectedSite.name}</p>
                    <p><strong>Type:</strong> {selectedSite.type.replace('_', ' ')}</p>
                    <p><strong>Address:</strong> {selectedSite.address}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Clock In/Out Times */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Timer className="inline h-4 w-4 mr-1 text-green-600" />
                Clock In Time *
              </label>
              <input
                type="datetime-local"
                name="clock_in_time"
                required
                value={formData.clock_in_time}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Timer className="inline h-4 w-4 mr-1 text-blue-600" />
                Clock Out Time
              </label>
              <input
                type="datetime-local"
                name="clock_out_time"
                value={formData.clock_out_time}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
              <p className="text-xs text-gray-500 mt-1">Leave empty if still working</p>
            </div>
          </div>

          {/* GPS Coordinates */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-4 flex items-center">
              <MapPin className="h-4 w-4 mr-2 text-gray-600" />
              GPS Coordinates (Optional)
            </h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h5 className="text-sm font-medium text-gray-700 mb-3">Clock In Location</h5>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Latitude</label>
                    <input
                      type="number"
                      name="clock_in_latitude"
                      step="0.000001"
                      min="-90"
                      max="90"
                      value={formData.clock_in_latitude}
                      onChange={handleChange}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500"
                      placeholder="-33.8688"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Longitude</label>
                    <input
                      type="number"
                      name="clock_in_longitude"
                      step="0.000001"
                      min="-180"
                      max="180"
                      value={formData.clock_in_longitude}
                      onChange={handleChange}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500"
                      placeholder="151.2093"
                    />
                  </div>
                </div>
              </div>

              <div>
                <h5 className="text-sm font-medium text-gray-700 mb-3">Clock Out Location</h5>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Latitude</label>
                    <input
                      type="number"
                      name="clock_out_latitude"
                      step="0.000001"
                      min="-90"
                      max="90"
                      value={formData.clock_out_latitude}
                      onChange={handleChange}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500"
                      placeholder="-33.8688"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Longitude</label>
                    <input
                      type="number"
                      name="clock_out_longitude"
                      step="0.000001"
                      min="-180"
                      max="180"
                      value={formData.clock_out_longitude}
                      onChange={handleChange}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500"
                      placeholder="151.2093"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Pay Rate and Hours Information */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h4 className="font-medium text-green-900 mb-4 flex items-center">
              <Timer className="h-4 w-4 mr-2 text-green-600" />
              Pay Rate & Hours Tracking
            </h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h5 className="text-sm font-medium text-gray-700 mb-3">Pay Rates ($/hr)</h5>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Hourly Rate</label>
                    <div className="relative">
                      <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500">$</span>
                      <input
                        type="number"
                        name="hourly_rate_used"
                        step="0.01"
                        min="0"
                        value={formData.hourly_rate_used}
                        onChange={handleChange}
                        className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder="35.50"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Overtime Rate</label>
                    <div className="relative">
                      <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500">$</span>
                      <input
                        type="number"
                        name="overtime_rate_used"
                        step="0.01"
                        min="0"
                        value={formData.overtime_rate_used}
                        onChange={handleChange}
                        className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder="53.25"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h5 className="text-sm font-medium text-gray-700 mb-3">Hours Breakdown</h5>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Regular Hours</label>
                    <input
                      type="number"
                      name="regular_hours"
                      step="0.01"
                      min="0"
                      value={formData.regular_hours}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      placeholder="8.00"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Overtime Hours</label>
                    <input
                      type="number"
                      name="overtime_hours"
                      step="0.01"
                      min="0"
                      value={formData.overtime_hours}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      placeholder="2.00"
                    />
                  </div>
                </div>
              </div>
            </div>

            {formData.hourly_rate_used && formData.regular_hours && (
              <div className="mt-4 pt-4 border-t border-green-200">
                <div className="text-sm space-y-1">
                  {formData.regular_hours && formData.hourly_rate_used && (
                    <p className="text-gray-700">
                      Regular Pay: <span className="font-semibold text-green-700">
                        ${(parseFloat(formData.regular_hours) * parseFloat(formData.hourly_rate_used)).toFixed(2)}
                      </span>
                    </p>
                  )}
                  {formData.overtime_hours && formData.overtime_rate_used && (
                    <p className="text-gray-700">
                      Overtime Pay: <span className="font-semibold text-orange-700">
                        ${(parseFloat(formData.overtime_hours) * parseFloat(formData.overtime_rate_used)).toFixed(2)}
                      </span>
                    </p>
                  )}
                  {formData.regular_hours && formData.hourly_rate_used && (
                    <p className="text-gray-900 font-semibold text-base pt-2 border-t border-green-300 mt-2">
                      Total Pay: ${(() => {
                        let total = parseFloat(formData.regular_hours) * parseFloat(formData.hourly_rate_used);
                        if (formData.overtime_hours && formData.overtime_rate_used) {
                          total += parseFloat(formData.overtime_hours) * parseFloat(formData.overtime_rate_used);
                        }
                        return total.toFixed(2);
                      })()}
                    </p>
                  )}
                </div>
              </div>
            )}

            <p className="text-xs text-gray-600 mt-3">
              Track the pay rates and hours for this specific timesheet entry. This allows for accurate cost tracking per site.
            </p>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="active">Active (Still Working)</option>
              <option value="completed">Completed</option>
            </select>
          </div>

          {/* Time Summary */}
          {formData.clock_in_time && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-2">Time Summary</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="font-medium text-blue-800">Clock In:</span>
                  <p className="text-blue-700">
                    {new Date(formData.clock_in_time).toLocaleString()}
                  </p>
                </div>
                {formData.clock_out_time && (
                  <div>
                    <span className="font-medium text-blue-800">Clock Out:</span>
                    <p className="text-blue-700">
                      {new Date(formData.clock_out_time).toLocaleString()}
                    </p>
                  </div>
                )}
                {formData.clock_in_time && formData.clock_out_time && (
                  <div>
                    <span className="font-medium text-blue-800">Total Hours:</span>
                    <p className="text-blue-700 font-semibold">
                      {calculateTotalHours().toFixed(2)} hours
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Breaks Management (only for existing timesheets) */}
          {timesheet?.id && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-medium text-gray-900 flex items-center">
                  <Coffee className="h-4 w-4 mr-2 text-gray-600" />
                  Breaks ({breaks.length})
                </h4>
                <button
                  type="button"
                  onClick={() => setShowAddBreak(!showAddBreak)}
                  className="flex items-center space-x-1 px-3 py-1 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors text-sm"
                >
                  <Plus className="h-3 w-3" />
                  <span>Add Break</span>
                </button>
              </div>

              {/* Add Break Form */}
              {showAddBreak && (
                <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
                  <h5 className="font-medium text-gray-900 mb-3">Add New Break</h5>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
                      <select
                        value={newBreak.break_type}
                        onChange={(e) => setNewBreak(prev => ({ ...prev, break_type: e.target.value as 'break' | 'lunch' }))}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500"
                      >
                        <option value="break">Break</option>
                        <option value="lunch">Lunch</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Start Time</label>
                      <input
                        type="datetime-local"
                        value={newBreak.start_time}
                        onChange={(e) => setNewBreak(prev => ({ ...prev, start_time: e.target.value }))}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">End Time</label>
                      <input
                        type="datetime-local"
                        value={newBreak.end_time}
                        onChange={(e) => setNewBreak(prev => ({ ...prev, end_time: e.target.value }))}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500"
                      />
                    </div>
                    <div className="flex items-end">
                      <button
                        type="button"
                        onClick={handleAddBreak}
                        disabled={!newBreak.start_time}
                        className="w-full px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 disabled:opacity-50 transition-colors"
                      >
                        Add
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Existing Breaks */}
              {breaks.length > 0 ? (
                <div className="space-y-2">
                  {breaks.map((breakRecord) => (
                    <div key={breakRecord.id} className="bg-white border border-gray-200 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className={`h-8 w-8 rounded-full flex items-center justify-center ${
                            breakRecord.break_type === 'lunch' ? 'bg-orange-100' : 'bg-blue-100'
                          }`}>
                            <Coffee className={`h-4 w-4 ${
                              breakRecord.break_type === 'lunch' ? 'text-orange-600' : 'text-blue-600'
                            }`} />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900 capitalize">
                              {breakRecord.break_type}
                            </p>
                            <div className="text-xs text-gray-600">
                              <p>Start: {new Date(breakRecord.start_time).toLocaleString()}</p>
                              {breakRecord.end_time && (
                                <p>End: {new Date(breakRecord.end_time).toLocaleString()}</p>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-3">
                          <div className="text-right">
                            <p className="text-sm font-medium text-gray-900">
                              {breakRecord.duration_minutes ? `${breakRecord.duration_minutes} min` : 'In progress'}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleDeleteBreak(breakRecord.id)}
                            disabled={deletingBreak === breakRecord.id}
                            className="text-red-600 hover:text-red-900 disabled:opacity-50"
                            title="Delete Break"
                          >
                            {deletingBreak === breakRecord.id ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4">
                  <Coffee className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">No breaks recorded for this timesheet</p>
                </div>
              )}
            </div>
          )}

          {/* Form Actions */}
          <div className="flex space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || loadingData}
              className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50 transition-colors"
            >
              {loading ? 'Saving...' : (timesheet ? 'Update Timesheet' : 'Create Timesheet')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};